/**
 * transformers.js `customCache` backend for EmbeddingGemma, persisted in the
 * Browser model store (IndexedDB). Implements the `match`/`put` subset of the
 * Web Cache API that `env.customCache` requires.
 *
 * Warm hit: `match` returns the stored bytes as a `Response` with no network
 * fetch. Cold miss: transformers.js fetches same-origin from `/model/...`,
 * then `put` persists the bytes with the baked model version. Stale entries
 * (version or size mismatch vs `modelManifest`) read as misses so the caller
 * refetches and replaces them. Every method never throws — storage failures
 * degrade to a cache miss and the network fetch proceeds in memory.
 */

import { log } from "../../logger";
import { getModelBytes, putModelBytes } from "./browserModelStore";
import { cacheKeyToModelPath, expectedSizeForKey, modelVersionForKey } from "./modelManifest";

function keyToString(request: string | URL | { url: string }): string {
  if (typeof request === "string") return request;
  if (request instanceof URL) return request.href;
  return request.url;
}

/**
 * Normalize a transformers.js cache key to its canonical same-origin model
 * path. Handles both the local-path shape (`/model/<dir>/<file>`, used for
 * reads and writes at runtime with `allowRemoteModels=false`) and the remote
 * URL shape (`https://<host>/<dir>/resolve/<rev>/<file>`, which transformers
 * also probes as a cache key) so both address the single stored entry.
 * Returns null for non-model keys, which are never stored.
 */
function normalizeKey(key: string): string | null {
  // Strip the remote `/resolve/<rev>/` infix first: the prefixed check below
  // would otherwise accept the resolve-infixed path as its own entry.
  const remote = key.match(/\/model\/(.+?)\/resolve\/[^/]+(\/.*)$/);
  if (remote) {
    return cacheKeyToModelPath(`/model/${remote[1]}${remote[2]}`);
  }
  return cacheKeyToModelPath(key);
}

function guessContentType(modelPath: string | null): string {
  if (modelPath?.endsWith(".json")) return "application/json";
  return "application/octet-stream";
}

/**
 * IndexedDB-backed Cache-API subset for transformers.js (`match`/`put`).
 * Only `/model/...` keys are stored; anything else is a miss / no-op so
 * unrelated requests never pollute the Browser model store.
 */
export class IndexedDBModelCache {
  /**
   * Serve a cached model file as a `Response` (warm hit, no network fetch).
   * Returns undefined on miss, stale entry (version/size mismatch vs the
   * baked manifest), non-model key, or any storage error — the caller then
   * fetches same-origin. Never throws.
   */
  async match(request: string | URL | { url: string }): Promise<Response | undefined> {
    const modelPath = normalizeKey(keyToString(request));
    if (!modelPath) return undefined;
    const version = modelVersionForKey(modelPath);
    if (!version) return undefined;
    const bytes = await getModelBytes(modelPath, version, expectedSizeForKey(modelPath));
    if (!bytes) return undefined;
    const headers = new Headers();
    headers.set("content-length", String(bytes.length));
    headers.set("content-type", guessContentType(modelPath));
    // Copy out of the structured-clone realm so `arrayBuffer()` downstream
    // always sees a plain Uint8Array.
    const copy = new Uint8Array(bytes.length);
    copy.set(bytes);
    return new Response(copy, { status: 200, headers });
  }

  /**
   * Persist a fetched model file under its canonical path (cold miss).
   * Non-model keys are ignored; storage failures degrade to a no-op so the
   * in-memory response still loads. Never throws.
   */
  async put(
    request: string | URL | { url: string },
    response: Response,
  ): Promise<void> {
    const modelPath = normalizeKey(keyToString(request));
    if (!modelPath) return;
    const version = modelVersionForKey(modelPath);
    if (!version) return;
    try {
      const clone = typeof response.clone === "function" ? response.clone() : response;
      const buffer = new Uint8Array(await clone.arrayBuffer());
      if (buffer.length === 0) return;
      await putModelBytes(modelPath, version, buffer);
    } catch (err) {
      log.warn({ err }, "[model-store] EmbeddingGemma cache write failed; continuing in memory");
    }
  }
}

/** Shared singleton wired into `env.customCache` by the embedding service. */
export const embeddingModelCache = new IndexedDBModelCache();
